// src/components/Universals/CustomerService/supportAdminPanel.component.jsx
import React, { useEffect, useRef, useState } from "react";
import HelpQuestionPanel from "./helpQuestionPanel.component.jsx";

const SupportAdminPanel = ({ currentUser, onClose }) => {
    const endpoint = "https://www.fbxchange.com/ReactPHP/supportChat.php";

    const [threads, setThreads] = useState([]);
    const [selectedThread, setSelectedThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState("");
    const [loadingReply, setLoadingReply] = useState(false);
    const [showClosedThreads, setShowClosedThreads] = useState(false);
    const [deletingThread, setDeletingThread] = useState(false);
    const [activeMode, setActiveMode] = useState("common");
    const messagesWrapRef = useRef(null);
    const shouldAutoScrollMessagesRef = useRef(true);

    const adminID = Number(currentUser?.scout ?? 0);
    const adminName =
        currentUser?.displayName ||
        currentUser?.userName ||
        currentUser?.name ||
        "Customer Service";

    const loadThreads = async () => {
        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: showClosedThreads
                        ? "getClosedSupportThreads"
                        : "getOpenSupportThreads",
                }),
            });

            const data = await res.json();
            const threadArray = Array.isArray(data?.threads) ? data.threads : [];
            setThreads(threadArray);

            if (selectedThread?.id) {
                const refreshed = threadArray.find(
                    (t) => Number(t.id) === Number(selectedThread.id)
                );
                if (refreshed) {
                    setSelectedThread(refreshed);
                } else {
                    setSelectedThread(null);
                    setMessages([]);
                }
            }
        } catch (err) {
            console.log("loadThreads error", err);
            setThreads([]);
        }
    };

    const loadMessages = async (threadID) => {
        if (!threadID) return;

        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "getThreadMessages",
                    threadID,
                    viewerType: "admin",
                    viewerUserID: adminID,
                }),
            });

            const data = await res.json();
            setMessages(Array.isArray(data?.messages) ? data.messages : []);
        } catch (err) {
            console.log("loadMessages error", err);
            setMessages([]);
        }
    };

    const markMessagesForBottomScroll = () => {
        shouldAutoScrollMessagesRef.current = true;
    };

    const handleMessagesScroll = () => {
        const messagesWrap = messagesWrapRef.current;
        if (!messagesWrap) return;

        const distanceFromBottom =
            messagesWrap.scrollHeight - messagesWrap.scrollTop - messagesWrap.clientHeight;
        shouldAutoScrollMessagesRef.current = distanceFromBottom < 40;
    };

    const sendReply = async () => {
        if (!selectedThread?.id || !reply.trim() || loadingReply || showClosedThreads) return;

        setLoadingReply(true);
        markMessagesForBottomScroll();

        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "sendSupportMessage",
                    threadID: selectedThread.id,
                    senderUserID: adminID,
                    senderName: adminName,
                    senderType: "admin",
                    message: reply.trim(),
                }),
            });

            const data = await res.json();

            if (data?.success) {
                setReply("");
                await loadMessages(selectedThread.id);
                await loadThreads();
            }
        } catch (err) {
            console.log("sendReply error", err);
        } finally {
            setLoadingReply(false);
        }
    };

    const hideThread = async () => {
        if (!selectedThread?.id || showClosedThreads) return;

        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "closeSupportThread",
                    threadID: selectedThread.id,
                    closedByUserID: adminID,
                    closedByName: adminName,
                }),
            });

            const data = await res.json();

            if (data?.success) {
                setSelectedThread(null);
                setMessages([]);
                await loadThreads();
            }
        } catch (err) {
            console.log("hideThread error", err);
        }
    };

    const deleteThread = async () => {
        if (!selectedThread?.id || !showClosedThreads || deletingThread) return;

        const confirmed = window.confirm(
            "Are you sure you want to permanently delete this thread and all messages in it?"
        );

        if (!confirmed) return;

        setDeletingThread(true);
        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "deleteSupportThread",
                    threadID: selectedThread.id,
                    deletedByUserID: adminID,
                    deletedByName: adminName,
                }),
            });

            const data = await res.json();

            if (data?.success) {
                setSelectedThread(null);
                setMessages([]);
                await loadThreads();
            }
        } catch (err) {
            console.log("deleteThread error", err);
        } finally {
            setDeletingThread(false);
        }
    };

    const handleReplyKeyDown = async (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!loadingReply && reply.trim() && !showClosedThreads) {
                await sendReply();
            }
        }
    };

    const formatMessageTime = (dateString) => {
        if (!dateString) return "";
        const date = new Date(String(dateString).replace(" ", "T"));
        if (Number.isNaN(date.getTime())) return dateString;

        return date.toLocaleString([], {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };

    useEffect(() => {
        setSelectedThread(null);
        setMessages([]);
        loadThreads();
        // eslint-disable-next-line
    }, [showClosedThreads]);

    useEffect(() => {
        if (showClosedThreads) return;

        loadThreads();
        const interval = setInterval(loadThreads, 5000);
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [showClosedThreads]);

    useEffect(() => {
        if (!selectedThread?.id) return;

        markMessagesForBottomScroll();
        loadMessages(selectedThread.id);

        if (showClosedThreads) return;

        const interval = setInterval(() => {
            loadMessages(selectedThread.id);
        }, 4000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [selectedThread?.id, showClosedThreads]);

    useEffect(() => {
        const messagesWrap = messagesWrapRef.current;
        if (!messagesWrap || !shouldAutoScrollMessagesRef.current) return;

        requestAnimationFrame(() => {
            messagesWrap.scrollTop = messagesWrap.scrollHeight;
        });
    }, [messages]);

    return (
        <div
            className="card shadow position-fixed"
            style={{
                bottom: "92px",
                right: "18px",
                width: "980px",
                height: "600px",
                zIndex: 99999,
                borderRadius: "14px",
                overflow: "hidden",
            }}
        >
            <div className="card-header d-flex justify-content-between align-items-center bg-primary text-white mb-0">
                <strong>
                    {activeMode === "common"
                        ? "Common Questions"
                        : activeMode === "manage"
                            ? "Manage Common Questions"
                        : `Customer Service Inbox ${showClosedThreads ? "- Closed Threads" : "- Open Threads"}`}
                </strong>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    onClick={onClose}
                >
                    X
                </button>
            </div>

            <div className="card-body p-2 d-flex flex-column" style={{ overflow: "hidden", minHeight: 0 }}>
                <div className="d-flex gap-2 mb-2">
                    <button
                        type="button"
                        className={`btn btn-sm flex-fill ${activeMode === "common" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setActiveMode("common")}
                    >
                        Common Questions
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm flex-fill ${activeMode === "inbox" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setActiveMode("inbox")}
                    >
                        Support Inbox
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm flex-fill ${activeMode === "manage" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setActiveMode("manage")}
                    >
                        Manage Questions
                    </button>
                </div>

                {activeMode === "common" ? (
                    <HelpQuestionPanel currentUser={currentUser} />
                ) : activeMode === "manage" ? (
                    <HelpQuestionPanel manageMode currentUser={currentUser} />
                ) : (
                <div className="d-flex h-100" style={{ minHeight: 0 }}>
                    <div
                        className="border-end p-2"
                        style={{ flex: "0 0 340px", width: "340px", overflowY: "auto", minHeight: 0 }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="fw-bold">
                                {showClosedThreads ? "Closed Threads" : "Open Threads"}
                            </div>
                            <button
                                type="button"
                                className={`btn btn-sm ${
                                    showClosedThreads
                                        ? "btn-warning"
                                        : "btn-outline-warning"
                                }`}
                                onClick={() => setShowClosedThreads((prev) => !prev)}
                            >
                                {showClosedThreads ? "Review Open Threads" : "Review Closed Threads"}
                            </button>
                        </div>

                        {threads.length === 0 ? (
                            <div className="text-muted small">
                                {showClosedThreads
                                    ? "No closed support questions."
                                    : "No open support questions."}
                            </div>
                        ) : (
                            threads.map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    className={`btn btn-sm w-100 text-start mb-2 ${
                                        Number(selectedThread?.id) === Number(t.id)
                                            ? "btn-primary"
                                            : "btn-outline-primary"
                                    }`}
                                    onClick={() => setSelectedThread(t)}
                                >
                                    <div className="fw-bold text-truncate">
                                        {t.displayName || t.userName || `User ${t.userID}`}
                                    </div>

                                    <div className="small text-truncate">
                                        {t.userName || `user${t.userID}`}
                                    </div>

                                    <div className="small text-truncate">
                                        {t.phoneNumber || "No phone on file"}
                                    </div>

                                    <div className="small text-truncate">
                                        {t.subject}
                                    </div>

                                    <div className="small text-truncate">
                                        Last: {formatMessageTime(t.lastMessageAt)}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>

                    <div className="flex-grow-1 d-flex flex-column p-2" style={{ minHeight: 0, minWidth: 0 }}>
                        {!selectedThread ? (
                            <div className="text-muted m-3">Select a thread.</div>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                                    <div>
                                        <div className="fw-bold">
                                            {selectedThread.displayName ||
                                                selectedThread.userName ||
                                                `User ${selectedThread.userID}`}
                                        </div>

                                        <div className="small text-muted">
                                            Username: {selectedThread.userName || `user${selectedThread.userID}`}
                                        </div>

                                        <div className="small text-muted">
                                            Phone: {selectedThread.phoneNumber || "No phone on file"}
                                        </div>

                                        <div className="small text-muted">
                                            Thread #{selectedThread.id} · {selectedThread.subject}
                                        </div>
                                    </div>

                                    {!showClosedThreads ? (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={hideThread}
                                        >
                                            Hide Thread
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={deleteThread}
                                            disabled={deletingThread}
                                        >
                                            {deletingThread ? "Deleting..." : "Delete This Thread"}
                                        </button>
                                    )}
                                </div>

                                <div
                                    ref={messagesWrapRef}
                                    className="flex-grow-1 border rounded p-2 mb-2 bg-light"
                                    onScroll={handleMessagesScroll}
                                    style={{ overflowY: "auto", minHeight: 0 }}
                                >
                                    {messages.length === 0 ? (
                                        <div className="text-muted small">No messages yet.</div>
                                    ) : (
                                        messages.map((m) => (
                                            <div
                                                key={m.id}
                                                className={`mb-2 p-2 rounded ${
                                                    m.senderType === "admin"
                                                        ? "alert alert-warning"
                                                        : "alert alert-primary"
                                                }`}
                                            >
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <div className="small fw-bold">
                                                        {m.senderType === "admin"
                                                            ? `Support: ${m.senderName || ""}`
                                                            : `${m.senderName || "User"}`}
                                                    </div>
                                                    <div className="text-muted small">
                                                        {formatMessageTime(m.createdAt)}
                                                    </div>
                                                </div>

                                                <div
                                                    className="small"
                                                    dangerouslySetInnerHTML={{
                                                        __html: String(m.message || "").replace(/\n/g, "<br/>"),
                                                    }}
                                                />
                                            </div>
                                        ))
                                    )}
                                </div>

                                {!showClosedThreads && (
                                    <>
                                        <textarea
                                            className="form-control mb-2"
                                            rows="4"
                                            value={reply}
                                            onChange={(e) => setReply(e.target.value)}
                                            onKeyDown={handleReplyKeyDown}
                                            placeholder="Type your reply... Press Enter to send, Shift+Enter for a new line."
                                        />

                                        <div className="d-flex justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={loadingReply || !reply.trim()}
                                                onClick={sendReply}
                                            >
                                                {loadingReply ? "Sending..." : "Reply"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
                )}
            </div>
        </div>
    );
};

export default SupportAdminPanel;
